import { isVipEligible } from "@/config/account";

import type { MsOrderInterface } from "@/types/account";
import type { LangObj, MsCustomer } from "@/types/types";

export const SUBJECT = {
  account: "Account Help",
  order: "Help With My Order",
  return: "Return",
  product: "Product Inquiries",
  partner: "Partner With MANSCAPED",
  other: "Other",
};

export const REASON = {
  account: {
    login: "Help Logging In",
    contact: "Update My Contact Information",
    payment: "Update My Payment Information",
    ...(isVipEligible ? { modifySub: "Modify My Subscription" } : {}),
    // TODO: Uncomment when cancel subscription hidden page is fixed with new subscription flow

    // ...(isVipEligible ? { cancelSub: "Cancel My Subscription" } : {}),
  },

  order: {
    where: "Where Is My Order?",
    cancel: "Cancel My Order",
    other: "Other Order Inquiries",
  },

  product: {
    warranty: "Warranty / Replacement",
    general: "General Product Inquiry",
  },

  partner: {
    campaign: "Influencer Campaign",
    marketing: "Marketing & PR",
    thirdParty: "Third-Party Partnership",
    donation: "Donation Requests",
    podcast: "Podcast Campaign",
    internationalDistributor: "International Distributor",
  },
};

export const COMPENSATION = [
  "Free product for posting",
  "$50 per post",
  "$100 per post",
  "$200 per post",
  "$300 per post",
  "$400 per post",
  "$500 per post",
  ">$1000 per post",
];

export const WARRANTY_CLAIM_TYPE = {
  poweredDevice: "Powered Device",
  poweredDeviceAccessories: "Powered Device Accessories",
  other: "Other",
} as const;

export const WARRANTY_ORDER_SOURCE = {
  manscaped: "Manscaped.com",
  amazon: "Amazon",
  retailer: "Retailer or Marketplace",
  other: "Other",
} as const;

export const BUSINESS_MODEL = [
  "3PL/4PL",
  "Distributor",
  "Sales Agency",
  "Both, Distributor & Sales Agency",
  "Retailer",
  "Other",
];

// dynamically overwritten in sites specific config
export const SUPPORT_EMAIL_ADDRESS_LOCALES = [{ lang: "en", value: "support@manscaped.com" }];

export const oosDisclaimer: OosDisclaimerProps = {
  text: [],
  checkboxRequired: false,
};

export const klaviyoOutOfStockListId = "";

export const gladlyChatEnabled = true;

export interface OosDisclaimerProps {
  text: LangObj[];
  checkboxRequired: boolean;
}

export interface SupportFormSubjectProps {
  account: string;
  order: string;
  return: string;
  product: string;
  partner: string;
  other: string;
}

export interface SupportFormAccountReasonProps {
  login: string;
  contact: string;
  payment: string;
  modifySub?: string;
  // cancelSub?: string;
}

export interface SupportFormOrderReasonProps {
  where: string;
  cancel: string;
  other: string;
}

export interface SupportFormProductReasonProps {
  warranty: string;
  general: string;
}

export interface SupportFormProps {
  subject: string;
  reason?: string | null;
  prioritySupport?: string;
  customer?: MsCustomer;
  orders?: MsOrderInterface[];
}

export interface SupportFormValues {
  subject: string;
  name: string;
  email: string;
  message?: string;
  lang: string;
  prioritySupport: string;
  mainContactReason?: string;
  subContactReason?: string;
  reason?: string;
  order?: string;
  qmId?: string;
}
export interface ContactAccountFormValues extends SupportFormValues {
  isFirstTimeLogin: string;
}

export interface ContactOrderFormValues extends SupportFormValues {
  receiveTrackingViaEmail: string | null;
}

export interface ContactReturnFormValues extends SupportFormValues {
  products: string;
  isPackageOpened: string | null;
}

export interface ContactProductFormValues extends SupportFormValues {
  products: string;
}

export interface InfluencerSignupValues {
  firstName: string;
  lastName: string;
  companyName: string;
  fullAddress: string;
  email: string;
  signature: string;
  locale: string;
}

export interface ContactPartnerInfluencerCampaignFormValues extends SupportFormValues {
  expectedCompensation: string;
  instagram: string;
  instagramFollowers: number;
  twitter: string;
  twitterFollowers: number;
  facebook: string;
  facebookFollowers: number;
  twitch: string;
  twitchFollowers: number;
  tiktok: string;
  tiktokFollowers: number;
  snapchat: string;
  snapchatFollowers: number;
  youtube: string;
  youtubeFollowers: number;
}

export interface ContactPartnerPodCastFormValues extends SupportFormValues {
  podcastName: string;
  podcastNote: string;
  podcastiTunesLink: string;
  podcastYouTube: string;
  podcastInstagram: string;
  podcastTiktok: string;
  podcastTwitter: string;
}

export interface ContactPartnerInternationalDistributorFormValues extends SupportFormProps {
  phone: string;
  businessModel: string;
  businessName: string;
  businessModelOtherDetail: string;
  businessWebsite: string;
  businessAddress: string;
  countries: string;
  brands: string;
  employees: string;
  proposedServices: string;
  hearAbout: string;
  interests: string;
  feedback: string;
}

export const initialSupportFormValues: SupportFormValues = {
  subject: "",
  prioritySupport: "",
  name: "",
  email: "",
  lang: "",
  reason: "",
  message: "",
  qmId: "",
};

export const initialCampaignValues: ContactPartnerInfluencerCampaignFormValues = {
  ...initialSupportFormValues,
  expectedCompensation: "",
  instagram: "",
  instagramFollowers: 0,
  twitter: "",
  twitterFollowers: 0,
  facebook: "",
  facebookFollowers: 0,
  twitch: "",
  twitchFollowers: 0,
  tiktok: "",
  tiktokFollowers: 0,
  snapchat: "",
  snapchatFollowers: 0,
  youtube: "",
  youtubeFollowers: 0,
};

export const initialPodCastValues: ContactPartnerPodCastFormValues = {
  ...initialSupportFormValues,
  podcastName: "",
  podcastNote: "",
  podcastiTunesLink: "",
  podcastYouTube: "",
  podcastInstagram: "",
  podcastTiktok: "",
  podcastTwitter: "",
};

export const initialInternationalDistributorValues: ContactPartnerInternationalDistributorFormValues =
  {
    ...initialSupportFormValues,
    phone: "",
    businessModel: "",

    businessModelOtherDetail: "",
    businessName: "",
    businessWebsite: "",
    businessAddress: "",
    countries: "",
    brands: "",
    employees: "",
    proposedServices: "",
    hearAbout: "",
    interests: "",
    feedback: "",
  };
